/**
 * noteByline.ts — the "who wrote this, and when" line under a note, in the
 * Slack/WhatsApp idiom the CTO asked for (2026-08-28): small, muted, and
 * carrying POSTED-or-EDITED rather than leaving the reader to infer it.
 *
 * WHY A PURE FUNCTION AND NOT JSX. The author half of the byline is already
 * a component (clinicians/noteAuthor.tsx) because it does an identity lookup
 * and renders a Badge. The TIME half needs neither, and both clinical panels
 * (AssessmentNotes.tsx, CaseNotes.tsx) had drifted into hand-rolling it
 * inline — the same two lines copy-pasted, which is how the two panels came
 * to disagree about anything in the first place. A string function is the
 * whole behaviour, so it can be unit-tested against real timestamps instead
 * of through a render tree.
 *
 * WHAT CHANGED, AND WHY EACH PART.
 *
 * 1. TIME, not just the date. Both panels rendered `formatUtcDate` — "15
 *    August 2026" — so two notes written four hours apart during one
 *    consultation were indistinguishable, and the ORDER a clinician recorded
 *    their reasoning in was unreadable. On a clinical record the sequence is
 *    often the point ("noted BP, then prescribed"), so the byline now uses
 *    `formatUtcDateTime`.
 *
 * 2. The word "Posted". A bare date is ambiguous about WHICH event it names —
 *    written? last changed? seen? — and that ambiguity got worse once an edit
 *    could also be stamped. Naming the event removes the guess.
 *
 * 3. WHEN it was edited, not merely THAT it was. `editedAt` has always been
 *    on the wire and both panels threw it away, rendering the bare word
 *    "edited". For a clinical note that is the wrong half to keep: a note
 *    rewritten ten minutes later is routine, and one rewritten three weeks
 *    later — after an outcome was known — is the thing an auditor is looking
 *    for. The two cases were previously identical on screen.
 *
 *    The edit time is shown INLINE rather than behind a hover. Slack hides it
 *    in a tooltip, but a tooltip does not exist on touch and is not
 *    discoverable by a reader who does not already suspect an edit — which is
 *    exactly the reader this is for. This is the one place the Slack idiom is
 *    deliberately NOT copied.
 *
 * 4. SAME-DAY EDITS COLLAPSE to a time. "15 August 2026, 09:30 · edited 15
 *    August 2026, 09:31" repeats eleven characters to convey one minute. When
 *    the edit lands on the posting day the byline shows "· edited 09:31" and
 *    nothing is lost; a different day keeps its full date, because that is
 *    the case that matters. This mirrors the attempt row in
 *    pipeline/DealDrawer.tsx, which already suppresses a repeated date the
 *    same way — the convention is the repo's, not a new invention.
 *
 * All times are UTC, matching every other timestamp in the portal
 * (lib/format.ts forces the zone) so nothing drifts by viewer locale.
 */
import { formatUtcDate, formatUtcDateTime } from './format';

/** The two timestamps any note or activity item carries on the wire. */
export interface NoteTimestamps {
  /** ISO-8601 creation stamp. */
  createdAt: string | undefined | null;
  /** ISO-8601 stamp of the last edit; absent/null on a note never edited. */
  editedAt?: string | undefined | null;
}

/**
 * The UTC wall-clock time alone, e.g. "09:31" — the same `h23` 24-hour clock
 * `formatUtcDateTime` uses, so a collapsed same-day edit reads identically to
 * the time it would have shown in full.
 */
function utcTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * Build the byline text for one note.
 *
 * - never edited → `Posted 15 August 2026, 09:30`
 * - edited same day → `Posted 15 August 2026, 09:30 · edited 09:31`
 * - edited later → `Posted 15 August 2026, 09:30 · edited 2 September 2026, 14:05`
 *
 * An unusable `createdAt` yields `''` rather than the string "Invalid Date" —
 * the panels render the byline unconditionally, so a bad stamp must degrade
 * to nothing visible rather than to garbage on a clinical record. An
 * unusable `editedAt` drops only the edit clause, keeping the posting stamp
 * that is still good.
 */
export function noteByline({ createdAt, editedAt }: NoteTimestamps): string {
  const posted = formatUtcDateTime(createdAt);
  if (!posted) return '';
  if (!editedAt) return `Posted ${posted}`;

  const editedFull = formatUtcDateTime(editedAt);
  if (!editedFull) return `Posted ${posted}`;

  // Compare the rendered DAY, not the raw instants: these are the exact
  // strings on screen, so the collapse can never disagree with what a reader
  // sees (a midnight-boundary edit stays expanded, which is correct).
  const sameDay = createdAt ? formatUtcDate(createdAt) === formatUtcDate(editedAt) : false;
  return `Posted ${posted} · edited ${sameDay ? utcTime(editedAt) : editedFull}`;
}
